import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { FormFieldGrid, missingRequired } from "../components/FormFields";
import type { FormValues } from "../components/FormFields";
import type { LogEntry, LogKind, OpenAction, ProjectDetail } from "../types";

/**
 * The site log and the meetings — dated records rather than routed documents.
 *
 * Two things drive the design. A daily log is **evidence**: it is written for
 * the day, signed off, and after that any change carries a note saying what was
 * amended, shown on the record. And a meeting is only worth recording here if
 * its decisions come out as **actions with an owner and a date** — otherwise a
 * Word file would do.
 */
export function SiteLogPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [kinds, setKinds] = useState<LogKind[]>([]);
  const [entries, setEntries] = useState<LogEntry[] | null>(null);
  const [actions, setActions] = useState<OpenAction[]>([]);
  const [open, setOpen] = useState<LogEntry | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const kindCode = params.get("kind") || "";
  const kind = useMemo(() => kinds.find((k) => k.code === kindCode) ?? kinds[0], [kinds, kindCode]);

  useEffect(() => {
    Promise.all([endpoints.getProject(id), endpoints.logsCatalog(), endpoints.openActions(id)])
      .then(([p, c, a]) => {
        setProject(p);
        setKinds(c.filter((k) => k.stage === "both" || k.stage === p.stage));
        setActions(a);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  const load = useCallback(() => {
    if (!kind) return;
    setEntries(null);
    endpoints
      .listLogs(id, kind.code)
      .then(setEntries)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id, kind]);

  useEffect(() => load(), [load]);

  function refreshActions() {
    endpoints.openActions(id).then(setActions).catch(() => undefined);
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!project || !kind)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  const canWrite = project.myAbilities.includes("write");

  return (
    <div className="page">
      <div className="crumb">
        <Link to="/projects">{t("projects.title")}</Link> <span className="muted">/ </span>
        <Link to={`/projects/${id}`}>{project.name}</Link>{" "}
        <span className="muted">/ {t("log.title")}</span>
      </div>

      <header className="page__head between">
        <div>
          <h1 className="page__title">{kind.title}</h1>
          <p className="page__sub">{kind.summary}</p>
        </div>
        {canWrite && (
          <Button size="sm" onClick={() => setAdding(true)}>
            {t("log.add", { what: kind.singular })}
          </Button>
        )}
      </header>

      <div className="segmented reg-tabs">
        {kinds.map((k) => (
          <button
            key={k.code}
            type="button"
            className={k.code === kind.code ? "seg is-on" : "seg"}
            onClick={() => setParams({ kind: k.code })}
          >
            {k.title}
          </button>
        ))}
      </div>

      {/* every open action on the project, so nobody opens six sets of minutes
          to find what is outstanding */}
      {actions.length > 0 && (
        <Card title={t("log.openActions", { count: actions.length })}>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("log.action")}</th>
                  <th>{t("log.owner")}</th>
                  <th>{t("log.dueBy")}</th>
                  <th>{t("log.from")}</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id} className={a.overdue ? "is-mine" : undefined}>
                    <td>{a.text}</td>
                    <td>{a.owner || "—"}</td>
                    <td className="mono">
                      {a.due || "—"}
                      {a.overdue && <Badge tone="danger">{t("forms.overdue")}</Badge>}
                    </td>
                    <td className="u-mail">
                      {a.entryTitle} · {a.entryDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        {entries === null ? (
          <div className="center">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <p className="muted">{t("log.empty", { what: kind.title.toLowerCase() })}</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("log.date")}</th>
                  <th>{t("reg.itemTitle")}</th>
                  {kind.hasActions && <th>{t("log.actions")}</th>}
                  <th>{t("log.signOff")}</th>
                  <th>{t("log.by")}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <button className="mono form-no linkish" onClick={() => setOpen(e)}>
                        {e.entryDate}
                      </button>
                    </td>
                    <td>
                      {e.title}
                      {e.amendments.length > 0 && (
                        <div className="u-mail">
                          {t("log.amendedN", { count: e.amendments.length })}
                        </div>
                      )}
                    </td>
                    {kind.hasActions && (
                      <td>
                        {e.openActions > 0 ? (
                          <Badge tone="warn">{t("log.nOpen", { count: e.openActions })}</Badge>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )}
                    <td>
                      {e.signedOff ? (
                        <Badge tone="good">{t("log.signedOff")}</Badge>
                      ) : (
                        <Badge tone="accent">{t("log.draft")}</Badge>
                      )}
                    </td>
                    <td className="u-mail">{e.signedOffBy || e.createdByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {adding && (
        <AddLogModal
          projectId={id}
          kind={kind}
          onClose={() => setAdding(false)}
          onAdded={(e) => {
            setAdding(false);
            setOpen(e);
            load();
            toast.success(t("log.added", { date: e.entryDate }));
          }}
        />
      )}
      {open && (
        <LogModal
          projectId={id}
          kind={kind}
          entry={open}
          onClose={() => {
            setOpen(null);
            load();
            refreshActions();
          }}
          onChanged={setOpen}
        />
      )}
    </div>
  );
}

function AddLogModal({
  projectId,
  kind,
  onClose,
  onAdded,
}: {
  projectId: string;
  kind: LogKind;
  onClose: () => void;
  onAdded: (e: LogEntry) => void;
}) {
  const { t } = useTranslation();
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState("");
  const [values, setValues] = useState<FormValues>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    const missing = missingRequired(kind.fields, values);
    if (missing.length) {
      setError(t("forms.required", { fields: missing.join(", ") }));
      return;
    }
    setBusy(true);
    setError("");
    try {
      onAdded(await endpoints.createLog(projectId, kind.code, { entryDate, title, data: values }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("log.add", { what: kind.singular })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <Field label={t("log.date")} hint={kind.onePerDate ? t("log.onePerDate") : ""}>
        <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
      </Field>
      {!kind.onePerDate && (
        <Field label={t("reg.itemTitle")}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      )}
      <FormFieldGrid
        fields={kind.fields}
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
      />
    </Modal>
  );
}

function LogModal({
  projectId,
  kind,
  entry,
  onClose,
  onChanged,
}: {
  projectId: string;
  kind: LogKind;
  entry: LogEntry;
  onClose: () => void;
  onChanged: (e: LogEntry) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<FormValues>(entry.data as FormValues);
  const [note, setNote] = useState("");
  const [actionText, setActionText] = useState("");
  const [actionOwner, setActionOwner] = useState("");
  const [actionDue, setActionDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (entry.signedOff && !note.trim()) {
      setError(t("log.needNote"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      onChanged(
        await endpoints.updateLog(projectId, kind.code, entry.id, {
          data: values, amendmentNote: note,
        }),
      );
      setEditing(false);
      setNote("");
      toast.success(t("common.save"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function signOff() {
    try {
      onChanged(await endpoints.signOffLog(projectId, kind.code, entry.id));
      toast.success(t("log.signedOff"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  async function addAction() {
    if (!actionText.trim()) return;
    try {
      onChanged(
        await endpoints.addAction(projectId, kind.code, entry.id, {
          text: actionText, owner: actionOwner, due: actionDue,
        }),
      );
      setActionText("");
      setActionOwner("");
      setActionDue("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  async function setActionStatus(actionId: string, status: string) {
    try {
      onChanged(
        await endpoints.updateAction(projectId, kind.code, entry.id, actionId, { status }),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Modal
      open
      title={`${entry.title || kind.singular} — ${entry.entryDate}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.close")}
          </Button>
          {entry.canEdit && !entry.signedOff && (
            <Button variant="outline" onClick={() => void signOff()}>
              {t("log.signOffNow")}
            </Button>
          )}
          {entry.canEdit &&
            (editing ? (
              <Button loading={busy} onClick={() => void save()}>
                {t("common.save")}
              </Button>
            ) : (
              <Button onClick={() => setEditing(true)}>{t("common.edit")}</Button>
            ))}
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}

      {entry.signedOff && (
        <div className="log-signed">
          {t("log.signedBy", { name: entry.signedOffBy, at: entry.signedOffAt.slice(0, 10) })}
        </div>
      )}

      {editing ? (
        <>
          {entry.signedOff && (
            <Field label={t("log.amendment")} hint={t("log.amendmentHint")}>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          )}
          <FormFieldGrid
            fields={kind.fields}
            values={values}
            onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
          />
        </>
      ) : (
        kind.fields.map((f) =>
          entry.data?.[f.key] ? (
            <div className="kv" key={f.key}>
              <span className="kv__k">{f.label}</span>
              <span className="kv__v log-val">{String(entry.data[f.key])}</span>
            </div>
          ) : null,
        )
      )}

      {kind.hasActions && (
        <>
          <h4 className="reg-h">{t("log.actions")}</h4>
          {entry.actions.length === 0 ? (
            <p className="muted">{t("log.noActions")}</p>
          ) : (
            <ul className="log-actions">
              {entry.actions.map((a) => (
                <li key={a.id} className={a.status === "Open" ? "log-act" : "log-act is-done"}>
                  <div className="log-act__text">{a.text}</div>
                  <div className="log-act__meta">
                    {a.owner && <span>{a.owner}</span>}
                    {a.due && <span className="mono">{a.due}</span>}
                    {a.overdue && <Badge tone="danger">{t("forms.overdue")}</Badge>}
                    {a.status !== "Open" && (
                      <span className="muted">
                        {a.status} · {a.closedBy} · {a.closedOn}
                      </span>
                    )}
                  </div>
                  {entry.canEdit && (
                    <Select
                      value={a.status}
                      onChange={(e) => void setActionStatus(a.id, e.target.value)}
                    >
                      {kind.actionStatuses.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  )}
                </li>
              ))}
            </ul>
          )}

          {entry.canEdit && (
            <div className="log-newact">
              <Input
                placeholder={t("log.newAction")}
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
              />
              <Input
                placeholder={t("log.owner")}
                value={actionOwner}
                onChange={(e) => setActionOwner(e.target.value)}
              />
              <Input type="date" value={actionDue} onChange={(e) => setActionDue(e.target.value)} />
              <Button size="sm" onClick={() => void addAction()}>
                {t("log.addAction")}
              </Button>
            </div>
          )}
        </>
      )}

      {entry.amendments.length > 0 && (
        <>
          <h4 className="reg-h">{t("log.amendments")}</h4>
          <ul className="log-amends">
            {entry.amendments.map((a, i) => (
              <li key={i}>
                <span className="mono">{a.at.slice(0, 16).replace("T", " ")}</span> · {a.by}
                <div>{a.note}</div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
